import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api/client'
import type { Inquiry } from '@/lib/api/types'
import { HelmOwnerCombobox } from '@/src/components/HelmOwnerCombobox'

/**
 * Who owns this inquiry, changeable in place like an assignee in Linear.
 * Assigning someone notifies them; clearing it puts the inquiry back in the
 * unassigned queue.
 */
export function InquiryOwnerPicker({
  inquiry,
  onChanged,
}: {
  inquiry: Pick<Inquiry, 'id' | 'owner_user_id'>
  onChanged: () => void
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function assign(ownerUserId: string) {
    if ((ownerUserId || null) === (inquiry.owner_user_id ?? null)) return
    setSaving(true)
    setError(null)
    try {
      await apiClient.updateInquiry(inquiry.id, { owner_user_id: ownerUserId || null })
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Couldn't change the owner")
    } finally {
      setSaving(false)
    }
  }

  return (
    // One shrinkable column: a long name and email truncate inside the
    // picker instead of widening it past the screen.
    <div className="grid w-full grid-cols-1 gap-1 sm:w-60">
      <Label htmlFor="inquiry-owner" className="text-xs text-muted-foreground">
        Owner
      </Label>
      <HelmOwnerCombobox
        id="inquiry-owner"
        value={inquiry.owner_user_id ?? ''}
        onValueChange={(value) => void assign(value)}
        disabled={saving}
        placeholder="Unassigned"
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
